/**
 * Utility for client-side image optimization to reduce load on Supabase Storage.
 * Balances quality and size.
 * Includes security checks (Magic Numbers) and size limits.
 */

// Constants for limits
export const MAX_IMAGE_SIZE_MB = 5;
export const MAX_VIDEO_SIZE_MB = 20;

const MAX_IMAGE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const MAX_VIDEO_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

interface CompressionOptions {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number; // 0.1 to 1.0
}

/**
 * Reads the first few bytes of a file to validate its Magic Number signature.
 */
const validateFileSignature = (file: File, type: 'image' | 'video'): Promise<boolean> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = (e) => {
            if (!e.target || !e.target.result) {
                resolve(false);
                return;
            }

            const arr = (new Uint8Array(e.target.result as ArrayBuffer)).subarray(0, 12);
            let header = "";
            for (let i = 0; i < arr.length; i++) {
                header += arr[i].toString(16).padStart(2, '0'); // Convert to hex
            }
            header = header.toLowerCase();

            // Image Signatures
            if (type === 'image') {
                if (header.startsWith('ffd8ff')) return resolve(true); // JPEG
                if (header.startsWith('89504e47')) return resolve(true); // PNG
                if (header.startsWith('47494638')) return resolve(true); // GIF
                if (header.startsWith('52494646') && header.includes('57454250')) return resolve(true); // WEBP (RIFF...WEBP)
            }

            // Video Signatures
            if (type === 'video') {
                // MP4/MOV (ftyp at offset 4 usually, check loosely or strictly)
                // 00 00 00 18 66 74 79 70 ...
                // Check if 'ftyp' (66 74 79 70) is present in first 12 bytes
                if (header.includes('66747970')) return resolve(true);
                if (header.startsWith('1a45dfa3')) return resolve(true); // WebM (EBML)
                if (header.startsWith('4f676753')) return resolve(true); // OGG
            }

            console.warn(`Invalid file signature: ${header}`);
            resolve(false);
        };
        reader.readAsArrayBuffer(file.slice(0, 12)); // Read only first 12 bytes
    });
};

export const validateVideo = async (file: File): Promise<void> => {
    // 1. Check Size
    if (file.size > MAX_VIDEO_BYTES) {
        throw new Error(`El video excede el tamaño máximo permitido de ${MAX_VIDEO_SIZE_MB}MB.`);
    }

    // 2. Check Magic Number
    const isValid = await validateFileSignature(file, 'video');
    if (!isValid) {
        throw new Error('Formato de video no válido o archivo corrupto.');
    }
};

export const compressImage = (file: File, options: CompressionOptions = {}): Promise<File> => {
    return new Promise(async (resolve, reject) => {
        const {
            maxWidth = 1920,
            maxHeight = 1080,
            quality = 0.8
        } = options;

        try {
            // 1. Check Size (Input)
            if (file.size > MAX_IMAGE_BYTES) {
                throw new Error(`La imagen excede el tamaño máximo permitido de ${MAX_IMAGE_SIZE_MB}MB.`);
            }

            // 2. Check Magic Number
            const isValidSig = await validateFileSignature(file, 'image');
            if (!isValidSig) {
                throw new Error('Formato de imagen no válido o archivo corrupto.');
            }

            // 3. Process Image
            const reader = new FileReader();
            reader.readAsDataURL(file);

            // Security: Sanitize filename
            const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_').replace(/\.[^/.]+$/, ""); // Remove extension too to force .webp

            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;

                img.onload = () => {
                    let width = img.width;
                    let height = img.height;

                    // Calculate new dimensions while maintaining aspect ratio
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    }
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Canvas context not available'));
                        return;
                    }

                    ctx.drawImage(img, 0, 0, width, height);

                    // Convert to blob (WebP if supported, otherwise JPEG)
                    const mimeType = 'image/webp';

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Canvas to Blob failed'));
                            return;
                        }

                        // Create a new File object
                        const newFile = new File([blob], `${safeName}.webp`, {
                            type: mimeType,
                            lastModified: Date.now(),
                        });

                        resolve(newFile);
                    }, mimeType, quality);
                };

                img.onerror = (err) => reject(new Error('Error al cargar la imagen para procesamiento.'));
            };

            reader.onerror = (err) => reject(new Error('Error al leer el archivo.'));

        } catch (error) {
            reject(error);
        }
    });
};
